import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { provideUserIdleConfig } from 'angular-user-idle';

import { routes } from './app.routes';
import { AppModule } from './app.module';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './auth/msal.setup';
import { PollingService } from './services/polling.service';
import { IDLE_TIME, PING_PERIOD, TIMEOUT_COUNT } from './app-config';
import { getReducers, metaReducers } from './reducers';
import { LoginEffects } from './effects/login.effects';
import { PollEffects } from './effects/poll.effects';

/** Brand spinner / checked-control color — Nova blue for form accents, green for spinners */
const SPINNER_COLOR = '#8bc63f';
const CHECKBOX_CHECKED_COLOR = '#007ad9';

const KairosTheme = definePreset(Lara, {
  components: {
    progressspinner: {
      colorScheme: {
        light: {
          root: {
            colorOne: SPINNER_COLOR,
            colorTwo: SPINNER_COLOR,
            colorThree: SPINNER_COLOR,
            colorFour: SPINNER_COLOR,
          },
        },
        dark: {
          root: {
            colorOne: SPINNER_COLOR,
            colorTwo: SPINNER_COLOR,
            colorThree: SPINNER_COLOR,
            colorFour: SPINNER_COLOR,
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          root: {
            checkedBackground: CHECKBOX_CHECKED_COLOR,
            checkedHoverBackground: '#005b9f',
            checkedBorderColor: CHECKBOX_CHECKED_COLOR,
            checkedHoverBorderColor: '#005b9f',
            checkedFocusBorderColor: CHECKBOX_CHECKED_COLOR,
          },
          icon: {
            checkedColor: '#ffffff',
            checkedHoverColor: '#ffffff',
          },
        },
        dark: {
          root: {
            checkedBackground: CHECKBOX_CHECKED_COLOR,
            checkedHoverBackground: '#005b9f',
            checkedBorderColor: CHECKBOX_CHECKED_COLOR,
            checkedHoverBorderColor: '#005b9f',
            checkedFocusBorderColor: CHECKBOX_CHECKED_COLOR,
          },
          icon: {
            checkedColor: '#ffffff',
            checkedHoverColor: '#ffffff',
          },
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideStore(getReducers(), { metaReducers }),
    provideEffects(LoginEffects, PollEffects),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: KairosTheme,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
    }),
    importProvidersFrom(AppModule, MsalModule),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    provideUserIdleConfig({ idle: IDLE_TIME, timeout: TIMEOUT_COUNT, ping: PING_PERIOD }),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    PollingService,
  ],
};
